import type { StoreData } from "@features/stores/types";
import type { ShippingPrice } from "@components/commerce/order/types";
import type { Cart } from "@graphql/generated/types";
import type { CheckoutErrorType } from "@lib/utils/checkout-utility/types";
import { useQueryClient } from "@tanstack/react-query";
import { CHECKOUT_ERRORS } from "@lib/utils/checkout-utility/types";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import {
    getCartValidationResult,
    type BasketValidationReason,
} from "@lib/utils/cart-utility/cartUtility";
import { useCartItemsAvailability } from "@hooks/useCartItemsAvailability";
import { useGetMasterDataForLineItems } from "@lib/commerce/hooks/cartOperations";
import { useStore } from "@store/store";
import { emailRegex, onlyNumbers } from "@lib/validators/regex/general";
import { phoneNumberRegex } from "@lib/validators/regex/phoneRegex";
import { activeSiteConfig } from "@config/site/site-config";
import { isClickAndCollectAddressValid } from "@lib/utils/checkout-utility/validateStoreData";
import { mapClickAndCollectStoreToDeliveryAddress } from "../../delivery-step/utils/mapPickupPointToAddress";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";
import { sanitizeRegionalAddressFields } from "@lib/utils/checkoutHelper";
import type { RegionalFieldFlags } from "@lib/utils/checkoutHelper";
import {
    fetchShippingMethods,
    useShippingMethods,
} from "@features/checkout/hooks/useShippingMethods";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { cartContainsNonExpressProducts } from "@lib/utils/cart-utility/nonExpressDeliveryProducts";
import { EXPRESS_FULFILLMENT_OPTION } from "../../delivery-step/delivery-methods/hooks/useDisplayedShippingMethods";

const PARCEL_SHOP_ID_FIELD = "address_CF_ParcelShopId";
const PARCEL_SHOP_CARRIER_FIELD = "address_CF_ParcelShopCarrier";
const PARCEL_TYPE_CODE_FIELD = "address_CF_ParcelTypeCode";

export const hasParcelShopIdForHomeDelivery = (
    cart: Cart,
    shippingMethods?: ShippingPrice[]
): boolean =>
    Boolean(
        shippingMethods?.some(
            (method) =>
                method.key === cart.shippingInfo?.shippingMethod?.key &&
                (method.orderType === "HD" || method.fulfillmentOption === "HD")
        )
    ) &&
    Boolean(
        cart.shippingAddress?.custom?.customFieldsRaw?.some(
            (field) =>
                (field.name === PARCEL_SHOP_ID_FIELD ||
                    field.name === PARCEL_SHOP_CARRIER_FIELD ||
                    field.name === PARCEL_TYPE_CODE_FIELD) &&
                typeof field.value === "string" &&
                field.value.trim().length > 0
        )
    );

export type CartCheckoutValidationResult =
    | { isValid: true }
    | {
          isValid: false;
          errorCode: CheckoutErrorType;
          errorValue?: string;
          reason?: BasketValidationReason;
          cartId?: string;
          skus?: string[];
      };

const fail = (
    errorCode: CheckoutErrorType,
    reason: BasketValidationReason,
    cartId: string,
    skus: string[],
    errorValue?: string
): Extract<CartCheckoutValidationResult, { isValid: false }> => ({
    isValid: false,
    errorCode,
    reason,
    cartId,
    skus,
    ...(errorValue !== undefined ? { errorValue } : {}),
});

export { isClickAndCollectAddressValid } from "@lib/utils/checkout-utility/validateStoreData";

type UseValidateBasketOptions = {
    /** Checkout v2 pushes add_payment_info when its payment step opens, so not again on Pay. */
    skipPaymentInfoEvent?: boolean;
};

export const useValidateBasket = ({
    skipPaymentInfoEvent = false,
}: UseValidateBasketOptions = {}) => {
    const isStateFieldEnabled = useFeatureFlag("isStateFieldEnabled");
    const isDistrictFieldEnabled = useFeatureFlag("isDistrictFieldEnabled");
    const getMasterProducts = useStore((state) => state.getMasterProducts);
    const getStoreData = useStore((state) => state.getStoreData);
    const getCart = useStore((state) => state.getCart);
    const getMasterDataForLineItems = useGetMasterDataForLineItems();
    const { checkItemsOnStock } = useCartItemsAvailability({ enabled: false });
    const queryClient = useQueryClient();
    const {
        data: shippingMethods,
        isSuccess: shippingMethodsLoaded,
        isError: shippingMethodsFailed,
    } = useShippingMethods();

    async function loadMasterData(cart: Cart, storeData: StoreData) {
        const skus = cart.lineItems.map((item) => item.variant.sku).filter(Boolean);
        const defaultChannelId = process.env.NEXT_PUBLIC_CHANNEL_ID;
        const storeChannelId = storeData?.supplyChannel?.[0]?.id;

        const channelIds = storeChannelId ? [defaultChannelId, storeChannelId] : [defaultChannelId];

        await getMasterDataForLineItems({ skus, channelIds });
    }

    const validateBasket = async (
        paymentMethod?: string,
        inputCart?: Cart,
        isPaymentContinuation = false
    ): Promise<CartCheckoutValidationResult> => {
        const cart = inputCart ?? getCart();
        if (!cart?.lineItems?.length) {
            return fail(CHECKOUT_ERRORS.CartInvalid, "empty_cart", cart?.id ?? "missing-cart", []);
        }

        const storeData = getStoreData();
        const skus = cart.lineItems.map((item) => item.variant.sku).filter(Boolean) as string[];
        const regionalFieldFlags: RegionalFieldFlags = {
            isStateFieldEnabled,
            isDistrictFieldEnabled,
        };

        await loadMasterData(cart, storeData);
        const masterProducts = getMasterProducts();

        const itemsOnStock = await checkItemsOnStock(cart.id, cart.lineItems);
        if (!itemsOnStock) {
            return fail(CHECKOUT_ERRORS.ItemsOutOfStock, "availability_api", cart.id, skus);
        }

        const cartValidation = getCartValidationResult(cart.lineItems, masterProducts);
        if (cartValidation.ok === false) {
            const errorCode =
                cartValidation.reason === "out_of_stock" ||
                cartValidation.reason === "quantity_exceeds_stock"
                    ? CHECKOUT_ERRORS.ItemsOutOfStock
                    : CHECKOUT_ERRORS.CartInvalid;
            return fail(errorCode, cartValidation.reason, cart.id, cartValidation.skus);
        }

        const shippingEmail = cart.shippingAddress?.email?.trim();

        if (!shippingEmail) {
            return { isValid: false, errorCode: CHECKOUT_ERRORS.MissingEmail, cartId: cart.id };
        }

        if (!emailRegex.test(shippingEmail)) {
            return {
                isValid: false,
                errorCode: CHECKOUT_ERRORS.EmailInvalid,
                errorValue: shippingEmail,
                cartId: cart.id,
            };
        }

        const countryCode = activeSiteConfig.checkout.countryCode;
        const shippingPhone = cart.shippingAddress?.phone?.trim()?.replace(countryCode, "");

        if (!shippingPhone) {
            return { isValid: false, errorCode: CHECKOUT_ERRORS.MissingPhone, cartId: cart.id };
        }

        const hasShippingPhoneRegex = phoneNumberRegex[cart.shippingAddress.country.toLowerCase()];
        if (
            hasShippingPhoneRegex
                ? !hasShippingPhoneRegex?.test(shippingPhone)
                : !onlyNumbers.test(shippingPhone)
        ) {
            return {
                isValid: false,
                errorCode: CHECKOUT_ERRORS.PhoneInvalid,
                errorValue: shippingPhone,
                cartId: cart.id,
            };
        }

        let methods = shippingMethods;
        let methodsLoaded = shippingMethodsLoaded;
        let methodsFailed = shippingMethodsFailed;
        // Pay can be pressed before the methods arrive; wait for them so the checks below still run.
        if (!methodsLoaded && !methodsFailed && !isPaymentContinuation) {
            try {
                methods = await queryClient.fetchQuery({
                    queryKey: [QueryKeys.getDeliveryMethods, { cartId: cart.id }],
                    queryFn: () => fetchShippingMethods(cart.id),
                });
                methodsLoaded = true;
            } catch {
                methodsFailed = true;
            }
        }

        const selectedShippingMethodKey = cart.shippingInfo?.shippingMethod?.key;
        const isSelectedMethodAvailable =
            methods?.some((method) => method.key === selectedShippingMethodKey) ?? false;

        if (
            (methodsFailed && !isPaymentContinuation) ||
            (methodsLoaded &&
                (!isSelectedMethodAvailable || hasParcelShopIdForHomeDelivery(cart, methods)))
        ) {
            return {
                isValid: false,
                errorCode: CHECKOUT_ERRORS.DeliveryMethodInvalid,
                cartId: cart.id,
            };
        }

        // The delivery step hides Express for these products, but a checkout left open while another
        // tab adds one still holds Express. This is the fresh cart, checked right before the charge.
        // A continuation is skipped: its payment was already submitted.
        const selectedShippingMethod = methods?.find(
            (method) => method.key === selectedShippingMethodKey
        );
        if (
            !isPaymentContinuation &&
            activeSiteConfig.hasNonExpressDeliveryProducts &&
            selectedShippingMethod?.fulfillmentOption === EXPRESS_FULFILLMENT_OPTION &&
            cartContainsNonExpressProducts(cart)
        ) {
            return {
                isValid: false,
                errorCode: CHECKOUT_ERRORS.ExpressUnavailableForProducts,
                cartId: cart.id,
            };
        }

        const expectedStoreAddress = storeData
            ? sanitizeRegionalAddressFields(
                  mapClickAndCollectStoreToDeliveryAddress(storeData),
                  regionalFieldFlags
              )
            : undefined;

        if (!isClickAndCollectAddressValid(cart, storeData, expectedStoreAddress)) {
            return {
                isValid: false,
                errorCode: CHECKOUT_ERRORS.CCStoreAddressInvalid,
                cartId: cart.id,
            };
        }

        // Only onSubmit knows the method. Continuations (3DS, redirect return) validate the same
        // attempt again and must not push a second add_payment_info without a payment_type.
        if (paymentMethod && !skipPaymentInfoEvent) {
            AnalyticsClient.addPaymentInfo(cart.lineItems, paymentMethod);
        }
        return { isValid: true };
    };

    return {
        validateBasket,
    };
};
